import { logger } from '../logger';

// Standard API error response format
export interface ApiError {
  message: string;
  code: string;
  status: number;
  details?: unknown;
}

// Define a type guard for Supabase-like errors
interface SupabaseError {
  message: string;
  code: string;
  status?: number;
  details?: unknown;
}

// Type guard for HTTP response errors
interface HttpError {
  status: number;
  statusText: string;
  data?: unknown;
}

// Function to normalize different error formats into a consistent ApiError
export const normalizeApiError = (error: unknown): ApiError => {
  // Handle Supabase error format
  if (error && typeof error === 'object' && 'code' in error && 'message' in error) {
    const supabaseError = error as SupabaseError;
    return {
      message: supabaseError.message,
      code: supabaseError.code,
      status: supabaseError.status || 500,
      details: supabaseError.details
    };
  }
  
  // Handle standard HTTP response errors
  if (error && typeof error === 'object' && 'status' in error && 'statusText' in error) {
    const httpError = error as HttpError;
    return {
      message: httpError.statusText,
      code: `HTTP_${httpError.status}`,
      status: httpError.status,
      details: httpError.data
    };
  }
  
  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      message: error.message,
      code: 'UNKNOWN_ERROR',
      status: 500,
      details: error
    };
  }
  
  // Default for unknown error formats
  return {
    message: 'An unknown error occurred',
    code: 'UNKNOWN_ERROR',
    status: 500,
    details: error
  };
};

// Common error messages for user-friendly display
export const getErrorMessage = (error: ApiError): string => {
  // Authentication errors
  if (error.status === 401) {
    return 'Your session has expired. Please sign in again.';
  }
  
  // Permission errors
  if (error.status === 403) {
    return 'You don\'t have permission to perform this action.';
  }
  
  // Not found errors
  if (error.status === 404) {
    return 'The requested resource was not found.';
  }
  
  // Validation errors
  if (error.status === 422) {
    return 'The submitted data is invalid. Please check your inputs and try again.';
  }
  
  // Rate limiting
  if (error.status === 429) {
    return 'Too many requests. Please try again later.';
  }
  
  // Server errors
  if (error.status >= 500) {
    return 'There was a problem with our server. Please try again later.';
  }
  
  // Default message
  return error.message || 'An unexpected error occurred. Please try again.';
};

// Log error to console or error tracking service
export const logApiError = (error: ApiError, context?: Record<string, unknown>): void => {
  logger.error('API Error:', {
    ...error,
    context,
    timestamp: new Date().toISOString()
  });
  
  // Here you would integrate with an error tracking service like Sentry
  // if (typeof window !== 'undefined' && window.Sentry) {
  //   window.Sentry.captureException(new Error(error.message), {
  //     extra: {
  //       code: error.code,
  //       status: error.status,
  //       details: error.details,
  //       context
  //     }
  //   });
  // }
}; 